import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Eye, EyeOff } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogClose,
} from '@/components/ui/Dialog';
import { api } from '@/lib/api/client';
import { useAuth } from '@/lib/auth/useAuth';
import { toast } from '@/components/ui/Toast';
import { useT } from '@/lib/i18n/useT';

/**
 * Self-service password change. POSTs to `/user/:id/change-password` (the
 * shared user-password endpoint); when the caller is changing their OWN
 * password the backend verifies `oldPassword` against the stored hash.
 */
export function ChangePasswordDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const t = useT();
  const { user } = useAuth();
  const userId = user?.id;
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setCurrent('');
      setNext('');
      setConfirm('');
      setShow({ current: false, next: false, confirm: false });
      setLocalError(null);
    }
  }, [open]);

  const change = useMutation({
    mutationFn: (v: { currentPassword: string; newPassword: string }) =>
      api.post<{ success: boolean; message: string }>(
        `/user/${userId}/change-password`,
        { oldPassword: v.currentPassword, newPassword: v.newPassword },
      ),
    onSuccess: () => {
      toast({ tone: 'success', title: t('changePassword.toast.updated') });
      onOpenChange(false);
    },
    onError: (e: Error) => setLocalError(e.message),
  });

  const matches = next.length > 0 && next === confirm;
  const long = next.length >= 8;
  const canSubmit = long && matches && !change.isPending;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (!long) return setLocalError(t('changePassword.error.length'));
    if (!matches) return setLocalError(t('changePassword.error.mismatch'));
    change.mutate({ currentPassword: current, newPassword: next });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[480px]">
        <header className="mb-5">
          <div
            className="mb-2 text-[11px] font-extrabold uppercase tracking-[0.10em] text-[var(--color-xk-accent)]"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {t('changePassword.eyebrow')}
          </div>
          <h2
            className="text-[20px] font-black tracking-tight text-[var(--color-xk-lavender)]"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {t('changePassword.title')}
          </h2>
          <p className="mt-1 text-[12px] text-[var(--color-xk-text-muted)]">
            {t('changePassword.subtitle')}
          </p>
        </header>

        <form onSubmit={submit} className="space-y-3">
          <PasswordField
            label={t('changePassword.field.current')}
            value={current}
            onChange={setCurrent}
            shown={show.current}
            onToggle={() => setShow((s) => ({ ...s, current: !s.current }))}
            autoFocus
            isCurrent
          />
          <PasswordField
            label={t('changePassword.field.new')}
            value={next}
            onChange={setNext}
            shown={show.next}
            onToggle={() => setShow((s) => ({ ...s, next: !s.next }))}
            hint={
              next.length === 0
                ? undefined
                : long
                  ? t('changePassword.hint.lengthOk')
                  : t('changePassword.hint.lengthShort', { count: next.length, missing: 8 - next.length })
            }
            hintTone={long ? 'ok' : 'warn'}
          />
          <PasswordField
            label={t('changePassword.field.confirm')}
            value={confirm}
            onChange={setConfirm}
            shown={show.confirm}
            onToggle={() => setShow((s) => ({ ...s, confirm: !s.confirm }))}
            hint={
              confirm.length === 0
                ? undefined
                : matches
                  ? t('changePassword.hint.match')
                  : t('changePassword.hint.noMatch')
            }
            hintTone={matches ? 'ok' : 'warn'}
          />

          {localError && (
            <div className="rounded-md border border-[var(--color-error)] bg-[rgb(239_68_68/0.08)] px-3 py-2 text-[12px] text-[var(--color-error)]">
              {localError}
            </div>
          )}

          <div className="mt-5 flex justify-end gap-3 border-t border-[var(--color-xk-border)] pt-4">
            <DialogClose asChild>
              <button
                type="button"
                className="rounded-lg border border-[var(--color-xk-border)] px-4 py-2 text-[13px] font-bold text-[var(--color-xk-lavender)] hover:bg-[var(--color-xk-surface)]"
              >
                {t('common.cancel')}
              </button>
            </DialogClose>
            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded-lg bg-[var(--color-xk-accent)] px-4 py-2 text-[13px] font-extrabold text-[#0A0A0A] transition-colors hover:bg-[var(--color-xk-accent-hover)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {change.isPending ? t('common.saving') : t('changePassword.submit')}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PasswordField({
  label,
  value,
  onChange,
  shown,
  onToggle,
  hint,
  hintTone,
  autoFocus,
  isCurrent,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  shown: boolean;
  onToggle: () => void;
  hint?: string;
  hintTone?: 'ok' | 'warn';
  autoFocus?: boolean;
  isCurrent?: boolean;
}) {
  const t = useT();
  return (
    <div>
      <label
        className="mb-1.5 block text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]"
        style={{ fontFamily: 'var(--font-display)' }}
      >
        {label}
      </label>
      <div className="relative">
        <input
          type={shown ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoFocus={autoFocus}
          autoComplete={isCurrent ? 'current-password' : 'new-password'}
          className="block w-full rounded-lg border border-[var(--color-xk-border)] bg-[rgb(255_255_255/0.05)] px-3 py-2.5 pr-10 text-[13px] tracking-wider text-[var(--color-xk-lavender)] focus:border-[var(--color-xk-accent)] focus:outline-none"
        />
        <button
          type="button"
          onClick={onToggle}
          tabIndex={-1}
          aria-label={shown ? t('changePassword.hide') : t('changePassword.show')}
          className="absolute right-2 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]"
        >
          {shown ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {hint && (
        <p
          className={`mt-1 text-[11px] ${
            hintTone === 'ok'
              ? 'text-[var(--color-xk-accent)]'
              : 'text-[var(--color-xk-text-muted)]'
          }`}
        >
          {hint}
        </p>
      )}
    </div>
  );
}
